<template>
  <b-overlay :show="loading">
    <div v-if="recently_accessed_applications.length" class="mb-5">
      <h1>Recently Used Applications</h1>
      <b-row v-if="recently_accessed_applications.length" gap="5" style="gap: 10px">
      <div
        v-for="(application, index) of recently_accessed_applications"
        :key="index"
        style="display: flex; flex-direction: row"
      >
        <div class="shadow p-2" style="max-width: 350px; gap: 10px">
          <div class="d-flex">
            <div>
              <b-media vertical-align="center">
                <template #aside>
                  <b-avatar
                    size="64"
                    :src="getValueFromSource(application, 'logo')"
                    :text="sentenceCase(avatarText(`${application.title}`))"
                    :variant="application.logo ? '' : 'primary'"
                    square
                  />
                </template>
              </b-media>
            </div>
            <div style="max-width: 200px; width: 200px;">
              <h1 v-b-tooltip.hover :title="application.title" class="text-ellipsis">{{ application.title }}</h1>
              <b-button
                variant="outline-primary"
                size="sm"
                @click="openApplicationHandler(application)"
              >
                Open
              </b-button>
              <span class="float-right" style="margin-top: 5px; display: inline-block;">{{ formatDistance(new Date(application.last_seen), new Date(), { addSuffix: true}) }}</span>
            </div>
          </div>
        </div>
      </div>
    </b-row>
    </div>

    <div v-if="applications.length">
      <h1>Applications</h1>
      <b-row gap="5" style="gap: 10px">
        <div
          v-for="(application, index) of applications"
          :key="index"
          style="display: flex; flex-direction: row"
        >
          <div class="shadow p-2" style="max-width: 350px; gap: 10px">
            <div class="d-flex">
              <div>
                <b-media vertical-align="center">
                  <template #aside>
                    <b-avatar
                      size="64"
                      :src="getValueFromSource(application, 'logo')"
                      :text="sentenceCase(avatarText(`${application.title}`))"
                      :variant="application.logo ? '' : 'primary'"
                      square
                    />
                  </template>
                </b-media>
              </div>
              <div style="max-width: 200px; width: 200px;">
                <h1 v-b-tooltip.hover class="text-ellipsis" variant="text" :title="application.title">{{ application.title }}</h1>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="openApplicationHandler(application)"
                >
                  Open
                </b-button>
                <span class="float-right" style="margin-top: 5px; display: inline-block;">
                  <b-popover :target="`${application._id}`" triggers="hover" placement="top">
                    <template #title>{{ application.title }}</template>
                    {{ application.description || 'No description' }}
                  </b-popover>
                  <feather-icon :id="`${application._id}`" icon="HelpCircleIcon" size="16" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </b-row>
    </div>
    <div v-else-if="!loading">
      <h1>No applications found.</h1>
    </div>
    <b-modal
      id="add-or-update"
      v-model="showApplicationAuthorizationModal"
      size="sm"
      centered
      hide-footer
      hide-header
      no-close-on-esc
      no-close-on-backdrop
      @hidden="() => { showApplicationAuthorizationModal = false }"
      @hide="() => { showApplicationAuthorizationModal = false }"
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header py-1 mb-2"
        style="border-bottom: 1px solid #000000"
      >
        <h5
          class="mb-0"
          style="font-weight: 400; font-size: 14px; line-height: 160%"
        >
          Authorize Application Access
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="() => { showApplicationAuthorizationModal = false }"
        />
      </div>
      <div class="text-center mt-1 mb-2">
        <p>An otp verification code has been sent to your number. Enter the code received below to proceed</p>
      </div>
      <validation-observer ref="authenticateForm" #default="{ invalid }">
        <b-form @submit.prevent="openApplication">
          <b-form-group label-for="code">
            <validation-provider
              #default="{ errors }"
              name="Code"
              vid="code"
              rules="required"
            >
              <div class="custom-input">
                <span class="leading">
                  <PasswordLock />
                </span>
                <input
                  v-model="code"
                  type="number"
                  class="otp"
                  placeholder="* * * * *"
                />
              </div>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <div>
            <b-button
              class="py-1"
              type="submit"
              variant="primary"
              block
              :disabled="invalid"
            >
              Grant Access
            </b-button>
          </div>
        </b-form>
      </validation-observer>
      <div class="text-center pt-1">
        <div class="">
          <p v-if="canResend" class="font-weight-bold border border-bottom m-0 p-0 cursor-pointer" @click="resendRequest"> Resend Code
          </p>
        </div>

        <div v-if="!canResend">
          <p class="m-0 p-0">
            Resend verification code after <span class="text-success">{{ timerString }}</span>
          </p>
        </div>
      </div>
    </b-modal>
  </b-overlay>
</template>

<script>
import { differenceInSeconds, formatDistance } from "date-fns";
import { get } from "lodash";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import {
  BOverlay,
  BRow,
  BCol,
  BAvatar,
  BMedia,
  BButton,
  BModal,
  BForm,
  BFormGroup,
  VBTooltip,
  VBTooltipPlugin,
  BPopover
} from "bootstrap-vue";
import { required } from "@validations";
import PasswordLock from "@/assets/svg/PasswordLock.svg";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  components: {
    BButton,
    BAvatar,
    BRow,
    BCol,
    BOverlay,
    BMedia,
    BModal,
    ValidationObserver,
    ValidationProvider,
    BForm,
    BFormGroup,
    PasswordLock,
    BPopover
  },
  directives: {
    'b-tooltip': VBTooltip,
    'b-tooltip-plugin': VBTooltipPlugin
  },
  data() {
    return {
      code: "",
      loading: false,
      applications: [],
      recently_accessed_applications: [],
      showApplicationAuthorizationModal: false,
      currentApplication: null,
      lastCodeRequest: null,
      timerString: '00:00',
      formErrorMessage: "",
      required,
      interval: null,
    };
  },
  computed: {
    canResend() {
      return this.timerString === "00:00";
    }
  },
  watch: {
    showApplicationAuthorizationModal: {
      handler() {
        this.code = ""
      }
    }
  },
  created() {
    this.fetchApplications();
  },
  methods: {
    async fetchApplications() {
      try {
        this.loading = true;

        const request =
          await this.useJwt().mainService.users.applications.list();
        const { data } = request.data;

        this.applications = data.applications;
        this.recently_accessed_applications = data.recently_accessed_applications;
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
        this.showListTable = true;
      }
    },
    async openApplication() {
      try {
        if (!this.currentApplication) {
          return;
        }
        this.loading = true;
        const application = this.currentApplication;

        const request = await this.useJwt().mainService.users.applications.open(
          application._id,
          { code: this.code }
        );
        const { data } = request.data;

        this.currentApplication = null;
        this.showApplicationAuthorizationModal = false;
        window.open(data.redirect_url, "_blank");
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        this.formErrorMessage = error_message
        if (error_message.includes("Too many attempts")) {
          this.showApplicationAuthorizationModal = false;
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
        this.showListTable = true;
      }
    },
    async requestAccess(application) {
      try {
        this.loading = true;

        const response = await this.useJwt().mainService.users.applications.requestAccess(
          application._id,
        );

        const { data } = response.data;

        this.currentApplication = application;
        this.showApplicationAuthorizationModal = true;

        if (this.interval) {
          clearInterval(this.interval);
        }

        this.lastCodeRequest = new Date(data.expires_in)
        this.startTimer(differenceInSeconds(this.lastCodeRequest, new Date()));

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success!",
            icon: "CheckCircleIcon",
            variant: "success",
            text: "Verification code sent to your number.",
          },
        });
      } catch (error) {
        const error_message =
        get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
        this.showListTable = true;
      }
    },
    resendRequest() {
      if (!this.currentApplication) { return }
      if (this.currentApplication.access_type === "external") {
        window.open(this.currentApplication.full_url, "_blank");
        return
      }
      this.requestAccess(this.currentApplication)
    },
    startTimer(duration) {
      let timer = duration;
      let minutes;
      let seconds;

      this.interval = setInterval(() => {
        minutes = parseInt(timer / 60, 10);
        seconds = parseInt(timer % 60, 10);

        minutes = minutes < 10 ? "0" + minutes : minutes;
        seconds = seconds < 10 ? "0" + seconds : seconds;

        // eslint-disable-next-line no-plusplus
        if (--timer < 0) {
          clearInterval(this.interval);
        }

        this.timerString = minutes + ":" + seconds;
      }, 1000);
    },
    openApplicationHandler(application) {
      this.currentApplication = application;

      if (application.access_type === "external") {
        window.open(application.full_url, "_blank");
        return
      }

      if (!application.require_otp) {
        this.openApplication();
      } else {
        this.requestAccess(application);
      }
    },
    formatDistance
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/dashboard-ecommerce.scss";
@import "@core/scss/vue/libs/chart-apex.scss";
</style>

<style scoped>
h3 {
  color: #459042;
}

.text-ellipsis {
  width: 200px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>

<style lang="scss">
@import '@core/scss/base/pages/app-general.scss';
@import '@core/scss/vue/pages/page-auth.scss';
</style>
